<template>
  <div class="program-supervisors-page my-3">
    <div class="card shadow-sm border-0 page-card">
      <div class="card-body">
        <div
          class="page-head d-flex justify-content-between align-items-center flex-wrap mb-3"
        >
          <div class="d-flex align-items-center">
            <span class="head-icon">
              <i class="bi bi-person-badge"></i>
            </span>
            <div>
              <h4 class="font-weight-bold mb-0">Program Supervisors</h4>
              <span class="page-subtitle">
                Select users from the system to assign as program supervisors.
              </span>
            </div>
          </div>
          <button
            v-if="canManage"
            type="button"
            class="btn btn-primary"
            @click="openCreate"
          >
            <i class="bi bi-plus-lg mr-1"></i> Add Supervisor
          </button>
        </div>

        <div class="toolbar d-flex flex-wrap align-items-center mb-3">
          <div class="search-box ml-auto">
            <i class="bi bi-search"></i>
            <input
              v-model="search"
              type="text"
              class="form-control"
              placeholder="Search name, email, role..."
            />
          </div>
          <span class="count-label ml-2 d-none d-md-inline">
            {{ filteredSupervisors.length }}
            {{ filteredSupervisors.length === 1 ? "supervisor" : "supervisors" }}
          </span>
        </div>

        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0 modern-table">
            <thead>
              <tr>
                <th style="width: 48px">#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Designation</th>
                <th>Status</th>
                <th
                  v-if="canManage"
                  class="text-center"
                  style="width: 1%; white-space: nowrap"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td :colspan="canManage ? 7 : 6" class="text-center py-5 text-muted">
                  <span class="spinner-border spinner-border-sm mr-2"></span>
                  Loading...
                </td>
              </tr>
              <tr v-else-if="!filteredSupervisors.length">
                <td :colspan="canManage ? 7 : 6" class="text-center py-5 text-muted">
                  <i class="bi bi-person-badge d-block empty-icon"></i>
                  No supervisors found.
                </td>
              </tr>
              <tr
                v-for="(supervisor, index) in filteredSupervisors"
                :key="supervisor.id"
              >
                <td>{{ index + 1 }}</td>
                <td class="font-weight-medium">{{ displayName(supervisor) }}</td>
                <td>{{ supervisor.user && supervisor.user.email ? supervisor.user.email : "—" }}</td>
                <td>
                  <span v-if="supervisor.user && supervisor.user.role" class="role-pill">
                    {{ roleLabel(supervisor.user.role) }}
                  </span>
                  <span v-else>—</span>
                </td>
                <td>{{ supervisor.designation || "—" }}</td>
                <td>
                  <span
                    class="status-pill"
                    :class="supervisor.is_active ? 'status-active' : 'status-inactive'"
                  >
                    {{ supervisor.is_active ? "Active" : "Inactive" }}
                  </span>
                </td>
                <td v-if="canManage" class="text-center text-nowrap">
                  <button
                    type="button"
                    class="btn btn-sm icon-action action-edit mr-1"
                    title="Edit supervisor"
                    @click="openEdit(supervisor)"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm icon-action action-delete"
                    title="Remove supervisor"
                    @click="confirmDelete(supervisor)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      id="supervisor-form-modal"
      tabindex="-1"
      role="dialog"
      aria-hidden="true"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <form @submit.prevent="submit">
            <div class="modal-header">
              <h5 class="modal-title mb-0">
                {{ editMode ? "Edit Supervisor" : "Add Supervisor" }}
              </h5>
              <button
                type="button"
                class="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label><span class="text-danger">*</span> User</label>
                <Multiselect
                  v-model="selectedUser"
                  :options="availableUsers"
                  track-by="id"
                  :custom-label="userLabel"
                  placeholder="Search and select a user…"
                  :searchable="true"
                  :allow-empty="false"
                  :disabled="saving"
                  open-direction="bottom"
                  class="user-select"
                />
                <small class="form-text text-muted">
                  Pick a user from User Management. Each user can only be added once.
                </small>
              </div>
              <div class="form-group">
                <label>Designation</label>
                <input
                  v-model="form.designation"
                  type="text"
                  class="form-control"
                  placeholder="Optional label shown in program list"
                />
              </div>
              <div class="form-group mb-0">
                <div class="custom-control custom-checkbox">
                  <input
                    id="supervisor-active"
                    v-model="form.is_active"
                    type="checkbox"
                    class="custom-control-input"
                  />
                  <label class="custom-control-label" for="supervisor-active">
                    Active (available for program assignment)
                  </label>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                data-dismiss="modal"
                :disabled="saving"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="saving || !selectedUser"
              >
                <span
                  v-if="saving"
                  class="spinner-border spinner-border-sm mr-1"
                ></span>
                {{ editMode ? "Save Changes" : "Add Supervisor" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import { roleLabel } from "./UserManagement/roles.js";

export default {
  name: "ProgramSupervisors",
  components: {
    Multiselect,
  },
  data() {
    return {
      supervisors: [],
      users: [],
      loading: false,
      saving: false,
      search: "",
      editMode: false,
      editingId: null,
      selectedUser: null,
      form: {
        designation: "",
        is_active: true,
      },
    };
  },
  computed: {
    canManage() {
      const role = this.$store.state.user && this.$store.state.user.role;
      return role === "Administrator" || role === "Supervisor";
    },
    assignedUserIds() {
      return new Set(
        this.supervisors
          .map((supervisor) => supervisor.user_id)
          .filter(Boolean)
      );
    },
    availableUsers() {
      return this.users.filter((user) => {
        if (this.editMode && this.selectedUser && user.id === this.selectedUser.id) {
          return true;
        }
        return !this.assignedUserIds.has(user.id);
      });
    },
    filteredSupervisors() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.supervisors;

      return this.supervisors.filter((supervisor) => {
        const name = this.displayName(supervisor).toLowerCase();
        const email = ((supervisor.user && supervisor.user.email) || "").toLowerCase();
        const role = ((supervisor.user && supervisor.user.role) || "").toLowerCase();
        const designation = (supervisor.designation || "").toLowerCase();
        return (
          name.includes(q) ||
          email.includes(q) ||
          role.includes(q) ||
          designation.includes(q)
        );
      });
    },
  },
  methods: {
    roleLabel,
    userLabel(user) {
      if (!user) return "";
      const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
      const role = user.role ? roleLabel(user.role) : "";
      const email = user.email || "";
      return [name, role, email].filter(Boolean).join(" · ");
    },
    displayName(supervisor) {
      if (supervisor.user) {
        return [supervisor.user.first_name, supervisor.user.last_name]
          .filter(Boolean)
          .join(" ");
      }
      return supervisor.name || "—";
    },
    resetForm() {
      this.form = {
        designation: "",
        is_active: true,
      };
      this.selectedUser = null;
      this.editingId = null;
      this.editMode = false;
    },
    openCreate() {
      this.resetForm();
      $("#supervisor-form-modal").modal("show");
    },
    openEdit(supervisor) {
      this.editMode = true;
      this.editingId = supervisor.id;
      this.form = {
        designation: supervisor.designation || "",
        is_active: supervisor.is_active !== false,
      };
      this.selectedUser =
        (supervisor.user &&
          this.users.find((user) => user.id === supervisor.user.id)) ||
        (supervisor.user_id &&
          this.users.find((user) => user.id === supervisor.user_id)) ||
        supervisor.user ||
        null;
      $("#supervisor-form-modal").modal("show");
    },
    getUsers() {
      axios
        .get("api/users/manage")
        .then((res) => {
          const data = Array.isArray(res.data.data) ? res.data.data : res.data;
          this.users = data || [];
        })
        .catch(() => {
          axios
            .get("api/users/internal")
            .then((res) => {
              this.users = res.data || [];
            })
            .catch(() => {
              toastr.error("Failed to load users.", "Error");
            });
        });
    },
    getSupervisors() {
      this.loading = true;
      axios
        .get("api/program-supervisors")
        .then((res) => {
          this.supervisors = res.data || [];
        })
        .catch(() => {
          toastr.error("Failed to load supervisors.", "Error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submit() {
      if (!this.selectedUser) {
        toastr.error("Please select a user.", "Error");
        return;
      }

      this.saving = true;
      const payload = {
        user_id: this.selectedUser.id,
        designation: this.form.designation.trim() || null,
        is_active: this.form.is_active,
      };

      const request = this.editMode
        ? axios.put(`api/program-supervisors/${this.editingId}`, payload)
        : axios.post("api/program-supervisors", payload);

      request
        .then((res) => {
          toastr.success(
            (res.data && res.data.msg) || "Saved successfully.",
            "Information"
          );
          $("#supervisor-form-modal").modal("hide");
          this.getSupervisors();
        })
        .catch((error) => {
          const message =
            (error.response &&
              error.response.data &&
              error.response.data.errors &&
              Object.values(error.response.data.errors)[0][0]) ||
            (error.response && error.response.data.message) ||
            (error.response && error.response.data.msg) ||
            "Something went wrong.";
          toastr.error(message, "Error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    confirmDelete(supervisor) {
      if (
        !confirm(
          `Remove "${this.displayName(supervisor)}" from program supervisors?`
        )
      ) {
        return;
      }

      axios
        .delete(`api/program-supervisors/${supervisor.id}`)
        .then((res) => {
          toastr.success(
            (res.data && res.data.msg) || "Removed successfully.",
            "Information"
          );
          this.getSupervisors();
        })
        .catch((error) => {
          const message =
            (error.response && error.response.data.message) ||
            "Failed to remove supervisor.";
          toastr.error(message, "Error");
        });
    },
  },
  mounted() {
    this.getUsers();
    this.getSupervisors();
  },
};
</script>

<style scoped>
.page-card {
  border-radius: 12px !important;
}

.page-head .head-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(77, 141, 255, 0.12);
  color: #4d8dff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-right: 0.85rem;
}

.page-subtitle {
  color: #768192;
  font-size: 0.88rem;
}

.toolbar .search-box {
  position: relative;
  min-width: 260px;
}

.toolbar .search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #768192;
}

.toolbar .search-box .form-control {
  padding-left: 2.2rem;
  border-radius: 10px;
  min-height: 40px;
}

.count-label {
  color: #768192;
  font-size: 0.88rem;
}

.modern-table thead th {
  border-top: none;
  border-bottom: 2px solid rgba(0, 0, 21, 0.06);
  color: #768192;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.35px;
  text-transform: uppercase;
}

.modern-table tbody td {
  vertical-align: middle;
  border-color: rgba(0, 0, 21, 0.05);
}

.font-weight-medium {
  font-weight: 600;
  color: #2b3a55;
}

.role-pill {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: rgba(77, 141, 255, 0.1);
  color: #4d8dff;
}

.status-pill {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
}

.status-active {
  background: rgba(46, 184, 92, 0.12);
  color: #2eb85c;
}

.status-inactive {
  background: rgba(118, 129, 146, 0.12);
  color: #768192;
}

.icon-action {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 21, 0.08);
  background: #fff;
  color: #768192;
}

.action-edit:hover {
  color: #4d8dff;
  border-color: rgba(77, 141, 255, 0.28);
  background: rgba(77, 141, 255, 0.08);
}

.action-delete:hover {
  color: #e55353;
  border-color: rgba(229, 83, 83, 0.28);
  background: rgba(229, 83, 83, 0.08);
}

.empty-icon {
  font-size: 1.6rem;
  margin-bottom: 0.35rem;
  opacity: 0.45;
}

.user-select >>> .multiselect__tags {
  min-height: 38px;
  padding-top: 6px;
  border-radius: 8px;
}

.user-select >>> .multiselect__single,
.user-select >>> .multiselect__input,
.user-select >>> .multiselect__placeholder {
  font-size: 0.88rem;
}

.user-select >>> .multiselect__option--highlight,
.user-select >>> .multiselect__option--highlight::after {
  background: #4d8dff;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
